import map from "lodash/map";
import range from "lodash/range";
import round from "lodash/round";
import convert from "convert-units";
import convertToRiR from "../../../../../../utils/converters/convert-intensity";
import { repsValue } from "../../../../../../utils/reps-range";
import { ActionsButtons } from "./ActionsButtons";
import { getLoadDrop } from "../../../../../../utils/load-drop";

const TableRow = ({
  exerciseId,
  set,
  handler,
  idx,
  hasSets = false,
  exerciseHasCustomRPE = false,
  primary = "squat",
  editDayMode = false,
  editable,
  repMaximums,
  addTempSet,
  removeSet,
  withWeight,
  fromMetric,
  isExerciseStarter = false,
  intensityMeasure,
  isRiR,
  units,
  addingSet,
  onAddSet,
  onSubmitSet,
  onDiscardSet,
  hasLoadDrop,
}) => {
  const { sets, reps, rpe, load, percentRepMax, repsLower, loadDrop } = set;
  const loadToShow = load > 0 ? fromMetric(load) : "";
  if (editDayMode && editable) {
    const exerciseRepMax = exerciseHasCustomRPE || repMaximums[primary];

    // getting the current idx since idx is not really an index but an `id-idx`
    const rowPosition = Number(idx.split("-")[1]);

    return (
      <tr key={idx}>
        <td className="sets">
          <input
            className="text-input"
            id={`sets-${exerciseId}-${idx}`}
            value={sets}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: "sets",
                exerciseId,
                setIdx: idx,
              })
            }
          />
        </td>
        <td>
          <input
            id={`reps-${exerciseId}-${idx}`}
            className="text-input"
            value={reps}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: "reps",
                exerciseId,
                setIdx: idx,
              })
            }
          />
        </td>
        <td className="rpe-rir">
          <select
            id={`rpe-${exerciseId}-${idx}`}
            className="select-input"
            name="rpe"
            value={rpe}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: "rpe",
                exerciseId,
                setIdx: idx,
              })
            }
          >
            <option value="">{intensityMeasure}</option>
            {map(range(4, 9.5, 0.5), (i) => (
              <option key={i} value={i + 1}>
                {convertToRiR(i + 1, !isRiR)}
              </option>
            ))}
            <option value="AMRAP">AMRAP</option>
          </select>
        </td>
        <td className="td-load" colSpan={hasLoadDrop ? 2 : 1}>
          {hasLoadDrop ? (
            <input
              className="!text-left"
              value={loadDrop}
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                handler({
                  value: evt.target.value,
                  field: "loadDrop",
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
          ) : (
            <input
              id={`load-${exerciseId}-${idx}`}
              className="text-input"
              defaultValue={loadToShow}
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                if (isNaN(evt.target.value)) return;
                let value = evt.target.value;
                if (units === "imperial" && value !== "") {
                  value = round(convert(value).from("lb").to("kg"), 2);
                }
                handler({
                  value,
                  field: "load",
                  otherNew: {
                    field: "percentRepMax",
                    value:
                      value === ""
                        ? ""
                        : ((parseFloat(value) / exerciseRepMax) * 100).toFixed(
                            2
                          ),
                  },
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
          )}
          <span className="units-label">
            {hasLoadDrop ? "% mod." : units === "imperial" ? "lb" : "kg"}
          </span>
        </td>
        {isExerciseStarter || hasLoadDrop ? null : (
          <td className="intensity td-intensity">
            <input
              id={`percentRepMax-${exerciseId}-${idx}`}
              className="text-input"
              value={
                String(percentRepMax).includes(".") &&
                String(percentRepMax).split(".")[1].length > 1
                  ? Math.round(percentRepMax * 10) / 10
                  : percentRepMax
              }
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                if (isNaN(evt.target.value)) return;
                handler({
                  value: evt.target.value,
                  field: "percentRepMax",
                  otherNew: {
                    field: "load",
                    value:
                      evt.target.value === ""
                        ? ""
                        : (
                            (parseFloat(evt.target.value) / 100) *
                            exerciseRepMax
                          ).toFixed(2),
                  },
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
            <span className="percentage-label">%</span>
          </td>
        )}

        <ActionsButtons
          extraCondition={hasSets}
          addingSet={addingSet}
          onAddSet={() => {
            addTempSet({ exerciseId, pos: idx.split("-")[1] });
            // Setting the addingSet index to the next row so the new set row
            // will have the actions buttons that appear below in the code
            onAddSet(rowPosition + 1);
          }}
          addSetImgSize={24}
          onRemoveSet={() => removeSet(exerciseId, idx.split("-")[1])}
          removeSetImgSize={18}
          onSubmitSet={onSubmitSet}
          onDiscardSet={() => {
            removeSet(exerciseId, idx.split("-")[1]);
            onDiscardSet();
          }}
          setIdx={rowPosition}
        />
      </tr>
    );
  }
  return (
    <tr key={idx}>
      <td className="sets">{sets}</td>
      <td>{repsValue(reps, repsLower)}</td>
      <td>{convertToRiR(rpe, !isRiR)}</td>
      <td>
        {loadDrop
          ? getLoadDrop(loadDrop)
          : load > 0
          ? `${withWeight(fromMetric(load))}`
          : "-"}
      </td>
      {isExerciseStarter ? null : (
        <td className="intensity">
          {percentRepMax > 0 ? `${round(percentRepMax, 1)}%` : "-"}
        </td>
      )}
    </tr>
  );
};

export default TableRow;
